import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, Music2, ImageIcon, AudioLines, Download, Sparkles,
  RefreshCw, Upload, Activity, Type, X, Plus, Wand2, Clapperboard, Video,
  Users, Layers, GripHorizontal, AlertTriangle,
} from "lucide-react";
import { mistApi, pipelinesApi, streamAsset } from "@/api/mist";
import UniversalSaveModal, { emergencyCommit } from "@/components/mist/UniversalSaveModal";
import { RenderProgress } from "@/components/mist/RenderProgress";
import ProfileGalleryPickButton from "@/components/mist/ProfileGalleryPickButton";
import { consumeMusicVizPrefill, setSongOrigin, consumeSongResult, consumeSingerToViz } from "@/lib/directorPrefill";
import ProjectNameCombobox from "@/components/mist/ProjectNameCombobox";

const ASPECTS = [
  ["9:16", "Vertical", "h-8 w-5"],
  ["1:1", "Square", "h-6 w-6"],
  ["16:9", "Wide", "h-5 w-8"],
];
const COLORS = [
  ["purple", "#B16CFF"], ["cyan", "#00F5D4"], ["gold", "#E0B341"],
  ["crimson", "#E63946"], ["white", "#FFFFFF"],
];
const DURATIONS = [[15, "15s"], [30, "30s"], [60, "60s"], ["full", "Full"]];

const btn = "flex items-center justify-center gap-1.5 border-2 border-black font-mono text-[10px] font-black uppercase tracking-widest active:translate-y-[1px]";
const box = "border-2 border-black bg-white shadow-[3px_3px_0px_#000]";

function fmtLen(d) {
  if (!d || d <= 0) return null;
  d = Math.round(d);
  if (d < 60) return `${d}s`;
  const m = Math.floor(d / 60), s = d % 60;
  if (s <= 2) return `${m}m`;
  if (s >= 58) return `${m + 1}m`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function MusicVisualizerStudio({ onExit }) {
  // ---- macro / flow ----
  const [projectName, setProjectName] = useState("");
  const [workspace, setWorkspace] = useState("video"); // video (WS1) | audio (WS2)
  const [step, setStep] = useState("setup");            // setup | align
  // ---- shared song / audio ----
  const [tracks, setTracks] = useState(null);
  const [songId, setSongId] = useState(null);
  const [songName, setSongName] = useState("");
  const [songDur, setSongDur] = useState(0);
  const [songSrc, setSongSrc] = useState(null);
  const [songLyrics, setSongLyrics] = useState(null);
  const [lyricsPaste, setLyricsPaste] = useState("");
  const [lyricsChecking, setLyricsChecking] = useState(false);
  // ---- WS1 video-with-music ----
  const [bgVideoId, setBgVideoId] = useState(null);
  const [bgVideoSrc, setBgVideoSrc] = useState(null);
  const [bgVideoBusy, setBgVideoBusy] = useState(false);
  const [overlayAudio, setOverlayAudio] = useState(false);
  const [audioSource, setAudioSource] = useState("");   // "" | device | community | singer
  const [vibePrompt, setVibePrompt] = useState("");       // WS1 style note (Phase 2) + WS2 scene brief
  // ---- WS2 audio-only ----
  const [durationMode, setDurationMode] = useState(30);   // 15 | 30 | 60 | "full"
  const [refImageId, setRefImageId] = useState(null);
  const [refImageSrc, setRefImageSrc] = useState(null);
  const [vibeStyle, setVibeStyle] = useState("auto");
  // ---- community audio picker ----
  const [commAudio, setCommAudio] = useState(null);
  const [commOpen, setCommOpen] = useState(false);
  // ---- look / captions (align trays) ----
  const [style, setStyle] = useState("waveform");         // waveform | none
  const [aspect, setAspect] = useState("9:16");
  const [color, setColor] = useState("purple");
  const [pulse, setPulse] = useState(true);
  const [lyricsScroll, setLyricsScroll] = useState(true);
  const [lyricStyle] = useState("karaoke");
  const [lyricTiming] = useState("synced");
  const [captions, setCaptions] = useState([]);
  // ---- lip-sync nudge (client-only in Phase 1) ----
  const [syncOffset, setSyncOffset] = useState(0);        // ms — displayed, sent as 0
  // ---- 3s Retention Intro Card (from Singer handoff) ----
  const [introCard, setIntroCard] = useState(null);       // {enabled, assetId, hookText}
  // ---- render / result ----
  const [busy, setBusy] = useState(false);
  const [stageMsg, setStageMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [finalUrl, setFinalUrl] = useState(null);
  const [finalId, setFinalId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  const bgVidRef = useRef(null);
  const audioRef = useRef(null);
  const refImgRef = useRef(null);
  const dragRef = useRef(null);

  // ------- guardrail -------
  const requireName = () => {
    if (projectName.trim()) return true;
    toast.error("Enter project name first");
    return false;
  };

  // ------- data loads -------
  const loadTracks = () => {
    setTracks(null);
    mistApi.libraryAssets("audio")
      .then((d) => {
        const items = d.items || [];
        setTracks(items);
        const missing = items.filter((t) => !(t.duration > 0)).map((t) => t.id);
        if (missing.length) {
          mistApi.libraryAssetDurations(missing)
            .then((res) => {
              const map = res?.durations || {};
              if (Object.keys(map).length) setTracks((prev) => (prev || []).map((t) => (map[t.id] > 0 ? { ...t, duration: map[t.id] } : t)));
            }).catch(() => {});
        }
      })
      .catch(() => setTracks([]));
  };
  useEffect(() => { loadTracks(); }, []);

  // Director-scene handoff (unchanged) → pre-load a video and jump to WS1.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    const pf = consumeMusicVizPrefill();
    if (!pf?.video_asset_id) return;
    hydratedRef.current = true;
    setWorkspace("video"); setAspect("9:16");
    setBgVideoId(pf.video_asset_id);
    (async () => { try { setBgVideoSrc(await streamAsset(pf.video_asset_id)); } catch { /* optional */ } })();
    toast.success("Director scene loaded — name it & add a track ♫");
  }, []);

  // Create-Song round-trip handback → the track lands in whichever source slot.
  useEffect(() => {
    const song = consumeSongResult();
    if (!song?.assetId) return;
    setSongId(song.assetId); setSongName(song.title || "New track"); setSongDur(0);
    setOverlayAudio(true); setAudioSource("singer");
    loadTracks();
    toast.success("Track added from Music Studio ♫");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Singer → Music Viz direct handoff (skipSetup): jump straight into the align /
  // styling canvas, pre-loaded with the decoupled video + master track + lyrics.
  useEffect(() => {
    const h = consumeSingerToViz();
    if (!h?.skipSetup || !h.video_asset_id) return;
    setWorkspace("video");
    setProjectName(h.project_name || "Singer Reel");
    setBgVideoId(h.video_asset_id);
    setOverlayAudio(true); setAudioSource("singer");
    if (h.song_asset_id) { setSongId(h.song_asset_id); setSongName(h.song_title || "Master track"); setSongDur(0); checkLyrics(h.song_asset_id); }
    const pm = h.project_metadata || {};
    if (pm.introCardEnabled) setIntroCard({ enabled: true, assetId: pm.introStaticAssetUrl || "", hookText: pm.centerHookText || "" });
    (async () => { try { setBgVideoSrc(await streamAsset(h.video_asset_id)); } catch { /* re-stream optional */ } })();
    setStep("align");
    toast.success("Decoupled reel loaded — fine-tune & render ♫");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Launch the full Mist Music Studio (origin-aware round-trip).
  const openMusicStudio = () => {
    if (!requireName()) return;
    const name = projectName.trim();
    setSongOrigin({ returnUrl: window.location.search.replace(/^\?/, ""), label: "Music Viz", projectName: name });
    window.history.pushState({}, "", `${window.location.pathname}?studio=music&view=studio`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const checkLyrics = (id) => {
    if (!id) { setSongLyrics(null); setLyricsPaste(""); return; }
    setLyricsChecking(true);
    return pipelinesApi.songLyrics(id)
      .then((d) => { setSongLyrics(d); if (d?.source !== "whisper") setLyricsPaste(d?.lyrics || ""); })
      .catch(() => setSongLyrics({ has_lyrics: false, lyrics: "", meta: {} }))
      .finally(() => setLyricsChecking(false));
  };

  // In the align step, stream the song to detect duration for the timeline.
  useEffect(() => {
    if (step !== "align" || !songId) { setSongSrc(null); return; }
    let alive = true;
    (async () => { try { const s = await streamAsset(songId); if (alive) setSongSrc(s); } catch { /* optional */ } })();
    return () => { alive = false; };
  }, [step, songId]);

  // ------- uploads / pickers -------
  const onBgVideoDevice = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f || !requireName()) return;
    setBgVideoBusy(true); setStageMsg("Uploading video…");
    try {
      const r = await pipelinesApi.uploadSeedVideo(f);
      const aid = r.asset_id || r.id;
      let src = null; try { src = await streamAsset(aid); } catch { src = URL.createObjectURL(f); }
      setBgVideoId(aid); setBgVideoSrc(src);
      toast.success("Video added ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Video upload failed"); }
    finally { setBgVideoBusy(false); setStageMsg(""); }
  };

  const onAudioDevice = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f || !requireName()) return;
    setBusy(true); setStageMsg("Uploading track…");
    try {
      const up = await pipelinesApi.uploadSeedAudio(f);
      setSongId(up.asset_id); setSongName(f.name); setSongDur(0);
      toast.success("Track added ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); setStageMsg(""); }
  };
  const loadCommAudio = () => {
    if (!requireName()) return;
    setCommOpen((o) => !o);
    if (commAudio === null) mistApi.communityVault().then((d) => setCommAudio(d.audio || [])).catch(() => setCommAudio([]));
  };
  const pickCommAudio = async (it) => {
    const t = toast.loading("Adding track…");
    try { const r = await mistApi.communityUse(it.id); setSongId(r.asset_id || r.id); setSongName(it.title || it.filename || "Shared track"); setSongDur(0); setCommOpen(false); toast.success("Track added ✓", { id: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add", { id: t }); }
  };

  const onRefImage = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f || !requireName()) return;
    setBusy(true); setStageMsg("Uploading reference…");
    try {
      const r = await pipelinesApi.uploadSeed(f);
      const aid = r.asset_id || r.id;
      let src = null; try { src = await streamAsset(aid); } catch { src = URL.createObjectURL(f); }
      setRefImageId(aid); setRefImageSrc(src);
      toast.success("Reference image set ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); setStageMsg(""); }
  };

  // ------- captions -------
  const addCaption = () => setCaptions((c) => (c.length >= 4 ? c : [...c, { text: "", at: "" }]));
  const updateCaption = (i, k, v) => setCaptions((c) => c.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeCaption = (i) => setCaptions((c) => c.filter((_, idx) => idx !== i));
  const cleanCaptions = () => captions
    .map((c) => ({ text: (c.text || "").trim(), at: Math.max(0, Number(c.at) || 0) }))
    .filter((c) => c.text).slice(0, 4);

  // ------- flow -------
  const canNext = workspace === "video" ? !!bgVideoId : !!songId;

  const goAlign = () => {
    if (!requireName()) return;
    if (workspace === "video" && !bgVideoId) { toast.error("Add your video first"); return; }
    if (workspace === "audio" && !songId) { toast.error("Add a track first"); return; }
    if (songId) checkLyrics(songId);
    setStep("align");
  };

  // ------- lip-sync nudge (client-only) -------
  const onWaveDown = (e) => {
    dragRef.current = { x: e.clientX ?? e.touches?.[0]?.clientX ?? 0, off: syncOffset };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onWaveMove = (e) => {
    if (!dragRef.current) return;
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const dx = cx - dragRef.current.x;
    setSyncOffset(Math.max(-2000, Math.min(2000, Math.round(dragRef.current.off + dx * 15))));
  };
  const onWaveUp = () => { dragRef.current = null; };

  // ------- render -------
  const generate = async () => {
    if (workspace === "video" && !overlayAudio) {
      toast.error("Toggle an overlay track — embedded transcription lands in Phase 2");
      return;
    }
    if (!songId) { toast.error("Add a track first"); return; }
    if (workspace === "audio" && !vibePrompt.trim()) { toast.error("Describe the video you want first"); return; }

    setBusy(true); setStageMsg("Starting render…"); setFinalUrl(null); setFinalId(null);
    try {
      let r;
      if (workspace === "video") {
        const payload = {
          music: songId, bg_video: bgVideoId, style, aspect, color, pulse,
          lyrics_scroll: lyricsScroll, lyric_style: lyricStyle, lyric_timing: lyricTiming,
        };
        if (lyricsScroll && !songLyrics?.has_lyrics && lyricsPaste.trim()) payload.lyrics_text = lyricsPaste.trim();
        const caps = cleanCaptions(); if (caps.length) payload.captions = caps;
        if (introCard?.enabled) payload.intro_card = { enabled: true, asset_id: introCard.assetId, hook_text: introCard.hookText, seconds: 3 };
        r = await pipelinesApi.musicVisualizer(payload);
      } else {
        const seconds = durationMode === "full" ? (songDur > 0 ? Math.round(songDur) : 180) : durationMode;
        const payload = {
          music: songId, vibe_prompt: vibePrompt.trim(), seconds,
          style: vibeStyle, viz_style: style, aspect, color, pulse,
          lyrics_scroll: lyricsScroll, lyric_style: lyricStyle, lyric_timing: lyricTiming,
          chyron: true, chyron_center: false,
        };
        if (lyricsScroll && !songLyrics?.has_lyrics && lyricsPaste.trim()) payload.lyrics_text = lyricsPaste.trim();
        const caps = cleanCaptions(); if (caps.length) payload.captions = caps;
        if (introCard?.enabled) payload.intro_card = { enabled: true, asset_id: introCard.assetId, hook_text: introCard.hookText, seconds: 3 };
        r = await pipelinesApi.musicVizVibe(payload);
      }
      const job = await pipelinesApi.poll(r.job_id, {
        interval: 2500, timeout: 1200000,
        onTick: (j) => { if (j?.stage) setStageMsg(String(j.stage)); if (typeof j?.progress === "number") setProgress(j.progress); },
      });
      if (job.status !== "completed" || !job.asset_id) throw new Error(job.error || "Render failed");
      let url = null;
      for (let a = 0; a < 4; a++) {
        try { url = await streamAsset(job.asset_id); break; }
        catch (err) { if (a === 3) throw err; await new Promise((res) => setTimeout(res, 1500 * (a + 1))); }
      }
      setFinalUrl(url); setFinalId(job.asset_id);
      toast.success("Your reel is ready — saved to your Vault ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Render failed");
    } finally { setBusy(false); setStageMsg(""); setProgress(0); }
  };

  const clearAndRestart = async () => {
    if (finalId && !saved) { try { await mistApi.discardAsset(finalId); } catch { /* ignore */ } }
    setFinalUrl(null); setFinalId(null); setSaved(false); setStep("setup");
  };
  useEffect(() => {
    if (!finalId || saved) return undefined;
    const h = () => emergencyCommit(finalId);
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [finalId, saved]);

  const previewRatio = aspect === "9:16" ? "9/16" : aspect === "1:1" ? "1/1" : "16/9";
  const colorHex = COLORS.find((c) => c[0] === color)?.[1] || "#B16CFF";
  const segments = songLyrics?.meta?.segments || [];
  const introSec = introCard?.enabled ? 3 : 0;               // +3s shift when a card is prepended
  const tlDur = (songDur || 30) + introSec;
  const tlWidth = Math.max(320, Math.round(tlDur * 26));

  // ============================ RESULT ============================
  if (finalUrl) {
    return (
      <div data-testid="music-visualizer-studio" className="flex h-full flex-col overflow-y-auto bg-[#FAF8F5]">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b-2 border-black bg-charcoal px-3 py-2.5">
          <button data-testid="viz-back" onClick={onExit} className="grid h-8 w-8 place-items-center border-2 border-gold bg-black text-gold"><ArrowLeft size={16} /></button>
          <div className="flex-1"><h2 className="flex items-center gap-1.5 font-serif text-base font-black leading-none text-white"><AudioLines size={15} className="text-gold" /> Music Visualizer</h2></div>
        </div>
        <div className="space-y-4 p-4">
          <div className="mx-auto w-full max-w-[260px] overflow-hidden border-2 border-black bg-black shadow-[4px_4px_0px_#000]" style={{ aspectRatio: previewRatio }}>
            <video data-testid="viz-result-video" src={finalUrl} controls autoPlay loop playsInline className="h-full w-full object-contain" />
          </div>
          <div className="flex gap-2">
            <button data-testid="viz-save" onClick={() => setSaveOpen(true)} className={`${btn} flex-1 bg-gold py-3 text-black`}><Download size={14} /> {saved ? "Saved ✓ · Save again" : "Save"}</button>
            <button data-testid="viz-clear-restart" onClick={clearAndRestart} className={`${btn} flex-1 bg-charcoal py-3 text-gold`}><RefreshCw size={14} /> New Reel</button>
          </div>
          <p className="text-center font-mono text-[8px] uppercase tracking-widest text-charcoal/50">{saved ? "Saved to your Vault" : "Auto-saves to your Vault if you leave — or tap Save"}</p>
          <UniversalSaveModal open={saveOpen} onClose={() => setSaveOpen(false)} assetId={finalId}
            track="VIDEOS" isMusic={false} defaultName={projectName ? `${projectName} reel` : (songName ? `${songName} reel` : "")}
            onSaved={() => setSaved(true)} />
        </div>
      </div>
    );
  }

  // ======================= SHARED HEADER =======================
  const Header = (
    <div className="sticky top-0 z-10 border-b-2 border-black bg-charcoal px-3 py-2.5">
      <div className="flex items-center gap-3">
        <button data-testid="viz-back" onClick={step === "align" ? () => setStep("setup") : onExit}
          className="grid h-8 w-8 shrink-0 place-items-center border-2 border-gold bg-black text-gold"><ArrowLeft size={16} /></button>
        <div className="min-w-0 flex-1">
          <ProjectNameCombobox value={projectName} onChange={setProjectName} testid="viz-project-name" maxLength={80}
            placeholder="Name this project…"
            className="w-full bg-transparent border-b-2 border-gold/40 focus:border-gold text-white font-serif text-base leading-tight outline-none" />
          <p className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.28em] text-white/40">Music Visualizer {step === "align" ? "· Align & Style" : "· Setup"}</p>
        </div>
      </div>
    </div>
  );

  // ============================ ALIGN ============================
  if (step === "align") {
    return (
      <div data-testid="music-visualizer-studio" className="flex h-full flex-col overflow-hidden bg-[#FAF8F5]">
        {Header}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
          {/* Fixed preview card */}
          <div className="mx-auto w-full max-w-[150px] overflow-hidden border-2 border-black bg-black" style={{ aspectRatio: previewRatio }}>
            <div className="relative h-full w-full">
              {workspace === "video" && bgVideoSrc
                ? <video src={bgVideoSrc} muted loop autoPlay playsInline className="h-full w-full object-cover" />
                : refImageSrc
                  ? <img src={refImageSrc} alt="ref" className="h-full w-full object-cover" style={{ animation: pulse ? "vizpulse 0.85s ease-in-out infinite" : "none" }} />
                  : <div className="grid h-full place-items-center text-white/40"><Wand2 size={22} /></div>}
              {style === "waveform" && (
                <div className="absolute inset-x-0 bottom-[6%] flex h-[24%] items-center justify-center gap-[2px] bg-black/25 px-2">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <span key={i} className="w-[2px] rounded-full" style={{ height: `${25 + Math.abs(Math.sin(i * 1.3)) * 60}%`, backgroundColor: colorHex, boxShadow: `0 0 6px ${colorHex}` }} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Multi-track lip-sync timeline */}
          <div className={`${box} p-2.5 space-y-2`} data-testid="viz-timeline">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60"><Layers size={12} /> Lip-sync timeline</p>
              <span data-testid="viz-sync-offset" className="border-2 border-black bg-charcoal px-1.5 py-0.5 font-mono text-[8px] font-black tabular-nums text-gold">
                {syncOffset >= 0 ? "+" : ""}{(syncOffset / 1000).toFixed(2)}s
              </span>
            </div>

            {workspace === "video" && !overlayAudio ? (
              <div className="border-2 border-dashed border-[#C2410C] bg-[#FFF4EC] p-3 text-center" data-testid="viz-phase2-placeholder">
                <AlertTriangle size={16} className="mx-auto mb-1 text-[#C2410C]" />
                <p className="font-mono text-[9px] font-black leading-relaxed text-[#C2410C]">
                  Embedded video transcription arriving in Phase 2. Please toggle an overlay track to generate synchronized text now.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto no-scrollbar">
                <div style={{ width: tlWidth }} className="relative space-y-1">
                  {introCard?.enabled && (
                    <div data-testid="viz-introcard-band" className="pointer-events-none absolute inset-y-0 left-0 z-10 border-r-2 border-[#FFE08A] bg-[#FFE08A]/15"
                      style={{ width: `${Math.min(100, (introSec / tlDur) * 100)}%` }}>
                      <span className="absolute left-1 top-1 bg-[#FFE08A] px-1 py-0.5 font-mono text-[7px] font-black uppercase text-black">Intro 0–3s</span>
                    </div>
                  )}
                  {/* Audio waveform — draggable to nudge (client-side preview only) */}
                  <div className="flex items-center gap-1">
                    <span className="w-4 shrink-0 text-center font-mono text-[9px]">📊</span>
                    <div data-testid="viz-wave-track" onPointerDown={onWaveDown} onPointerMove={onWaveMove} onPointerUp={onWaveUp} onPointerCancel={onWaveUp}
                      className="relative h-9 flex-1 cursor-ew-resize touch-none select-none overflow-hidden border-2 border-black bg-[#0d0d0d]">
                      <div className="absolute inset-y-0 flex items-center gap-[2px] px-1" style={{ transform: `translateX(${syncOffset / 15}px)` }}>
                        {Array.from({ length: Math.max(24, Math.round(tlWidth / 6)) }).map((_, i) => (
                          <span key={i} className="w-[2px] rounded-full" style={{ height: `${20 + Math.abs(Math.sin(i * 0.7)) * 70}%`, backgroundColor: colorHex, opacity: 0.9 }} />
                        ))}
                      </div>
                      <span className="absolute right-1 top-1 flex items-center gap-0.5 bg-black/60 px-1 font-mono text-[7px] font-black uppercase text-white/70"><GripHorizontal size={9} /> drag</span>
                    </div>
                  </div>
                  {/* Video / scene frames */}
                  <div className="flex items-center gap-1">
                    <span className="w-4 shrink-0 text-center font-mono text-[9px]">🎞️</span>
                    <div className="flex h-8 flex-1 items-center gap-[2px] overflow-hidden border-2 border-black bg-[#151515] px-1" data-testid="viz-frames-track">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <span key={i} className="h-6 flex-1 border border-white/10" style={{ background: workspace === "video" ? "#222" : `linear-gradient(135deg, ${colorHex}44, #111)` }} />
                      ))}
                    </div>
                  </div>
                  {/* Whisper lyric segments (line-level in Phase 1) */}
                  <div className="flex items-center gap-1">
                    <span className="w-4 shrink-0 text-center font-mono text-[9px]">📝</span>
                    <div className="relative h-9 flex-1 overflow-hidden border-2 border-black bg-white" data-testid="viz-lyric-track">
                      {lyricsChecking ? (
                        <span className="flex h-full items-center gap-1.5 px-2 font-mono text-[9px] text-charcoal/60"><Loader2 size={11} className="animate-spin" /> Reading vocal…</span>
                      ) : segments.length > 0 && songDur > 0 ? (
                        segments.slice(0, 60).map((s, i) => {
                          const left = Math.max(0, ((s.start + introSec) / tlDur) * 100);
                          const w = Math.max(4, ((s.end - s.start) / tlDur) * 100);
                          return (
                            <span key={i} data-testid={`viz-seg-${i}`} title={s.text}
                              className="absolute top-1 bottom-1 overflow-hidden truncate border border-black bg-gold px-1 font-mono text-[8px] font-black leading-[26px] text-black"
                              style={{ left: `${left}%`, width: `${w}%` }}>{s.text}</span>
                          );
                        })
                      ) : segments.length > 0 ? (
                        <div className="flex h-full items-center gap-1 px-1">
                          {segments.slice(0, 30).map((s, i) => (
                            <span key={i} data-testid={`viz-seg-${i}`} className="shrink-0 border border-black bg-gold px-1 py-0.5 font-mono text-[8px] font-black text-black">{s.text}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="flex h-full items-center px-2 font-mono text-[9px] text-charcoal/50">No detected lyrics — paste them in Setup, or they'll be blank.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <p className="font-mono text-[7px] leading-relaxed text-charcoal/40">
              Drag the waveform to preview a nudge. Per-word timing, inline word edits &amp; applied millisecond offset land in Phase 2 — this render uses the auto-synced timing.
            </p>
          </div>

          {/* Toolbelt trays */}
          {/* Canvas ratio */}
          <div className={`${box} p-2.5`} data-testid="viz-tray-canvas">
            <p className="mb-1.5 font-mono text-[8px] font-black uppercase tracking-widest text-charcoal/50">Canvas</p>
            <div className="flex gap-1.5">
              {ASPECTS.map(([val, label, dims]) => (
                <button key={val} data-testid={`viz-aspect-${val.replace(":", "-")}`} onClick={() => setAspect(val)}
                  className={`flex flex-1 flex-col items-center gap-1 border-2 border-black py-2 ${aspect === val ? "bg-gold text-black ring-2 ring-black" : "bg-white text-charcoal"}`}>
                  <div className={`${dims} border-2 border-black bg-charcoal`} />
                  <span className="font-mono text-[8px] font-black uppercase">{label}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Audio / wave control */}
          <div className={`${box} p-2.5 space-y-2`} data-testid="viz-tray-audio">
            <p className="font-mono text-[8px] font-black uppercase tracking-widest text-charcoal/50">Sound wave</p>
            <div className="flex gap-1.5">
              <button data-testid="viz-style-waveform" onClick={() => setStyle("waveform")} className={`${btn} flex-1 py-2 ${style === "waveform" ? "bg-gold text-black" : "bg-white text-charcoal"}`}><AudioLines size={12} /> On</button>
              <button data-testid="viz-style-none" onClick={() => setStyle("none")} className={`${btn} flex-1 py-2 ${style === "none" ? "bg-gold text-black" : "bg-white text-charcoal"}`}><ImageIcon size={12} /> Off</button>
              <button data-testid="viz-toggle-pulse" onClick={() => setPulse((p) => !p)} aria-pressed={pulse} className={`${btn} flex-1 py-2 ${pulse ? "bg-[#B16CFF] text-white" : "bg-white text-charcoal"}`}><Activity size={12} /> Pulse</button>
            </div>
            <div className="flex items-center gap-1.5">
              {COLORS.map(([name, hex]) => (
                <button key={name} data-testid={`viz-color-${name}`} onClick={() => setColor(name)}
                  className={`h-7 flex-1 border-2 ${color === name ? "border-black ring-2 ring-gold" : "border-black/30"}`} style={{ backgroundColor: hex }} aria-label={name} />
              ))}
            </div>
          </div>
          {/* Captions / lyrics */}
          <div className={`${box} p-2.5 space-y-2`} data-testid="viz-tray-captions">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[8px] font-black uppercase tracking-widest text-charcoal/50">Captions</p>
              <button data-testid="viz-toggle-lyrics" onClick={() => setLyricsScroll((v) => !v)} aria-pressed={lyricsScroll}
                className={`${btn} px-2 py-1 ${lyricsScroll ? "bg-[#00A6A6] text-white" : "bg-white text-charcoal"}`}><Type size={11} /> Lyrics {lyricsScroll ? "On" : "Off"}</button>
            </div>
            {captions.map((c, i) => (
              <div key={i} className="flex items-center gap-1.5" data-testid={`viz-caption-row-${i}`}>
                <input data-testid={`viz-caption-text-${i}`} value={c.text} maxLength={160} onChange={(e) => updateCaption(i, "text", e.target.value)} placeholder={`Caption ${i + 1}`}
                  className="min-w-0 flex-1 border-2 border-black bg-white px-2 py-1.5 font-mono text-[10px] outline-none" />
                <div className="flex shrink-0 items-center gap-1 border-2 border-black bg-white px-1.5 py-1.5">
                  <input data-testid={`viz-caption-at-${i}`} type="number" min={0} step={1} value={c.at} onChange={(e) => updateCaption(i, "at", e.target.value)} placeholder="0" className="w-9 bg-transparent text-right font-mono text-[10px] outline-none" />
                  <span className="font-mono text-[9px] font-black text-charcoal/50">s</span>
                </div>
                <button data-testid={`viz-caption-remove-${i}`} onClick={() => removeCaption(i)} className="grid h-7 w-7 shrink-0 place-items-center border-2 border-black bg-white text-charcoal"><X size={13} /></button>
              </div>
            ))}
            {captions.length < 4 && (
              <button data-testid="viz-caption-add" onClick={addCaption} className={`w-full ${btn} bg-white py-2 text-charcoal`}><Plus size={12} /> Add caption</button>
            )}
          </div>

          {/* Generate */}
          {introCard?.enabled && (
            <div className={`${box} p-2.5 space-y-2`} data-testid="viz-tray-introcard">
              <p className="font-mono text-[8px] font-black uppercase tracking-widest text-charcoal/50">Intro card · 0–3s hook</p>
              <input value={introCard.hookText} onChange={(e) => setIntroCard((c) => ({ ...c, hookText: e.target.value }))} data-testid="viz-introcard-hook" maxLength={80}
                placeholder="Centre hook text…" className="w-full border-2 border-black bg-white px-2 py-2 font-mono text-[11px] outline-none" />
              <p className="font-mono text-[7px] leading-relaxed text-charcoal/40">Burns a 3s static title card at the start (server-side drawtext) — the highlighted 0–3s band shows where it sits.</p>
            </div>
          )}
          <button data-testid="viz-generate" onClick={generate} disabled={busy || (workspace === "video" && !overlayAudio) || !songId || (workspace === "audio" && !vibePrompt.trim())}
            className={`${btn} w-full bg-charcoal py-4 text-gold disabled:opacity-50`}>
            {busy ? <><Loader2 size={15} className="animate-spin" /> {stageMsg || "Rendering…"}</> : <><Sparkles size={15} /> Generate Reel</>}
          </button>
          {busy && (
            <div className="space-y-1.5" data-testid="viz-progress">
              <RenderProgress value={progress} active={busy} label={stageMsg || "Rendering your reel"} testid="viz-render-progress" />
              <p className="text-center font-mono text-[8px] uppercase tracking-widest text-charcoal/40">Full-length renders can take a couple of minutes.</p>
            </div>
          )}
        </div>
        <style>{`@keyframes vizpulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}`}</style>
      </div>
    );
  }

  // ============================ SETUP ============================
  return (
    <div data-testid="music-visualizer-studio" className="flex h-full flex-col overflow-hidden bg-[#FAF8F5]">
      {Header}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {/* Macro switch */}
        <div className="grid grid-cols-2 gap-1.5" data-testid="viz-workspace-switch">
          <button data-testid="viz-ws-video" onClick={() => setWorkspace("video")}
            className={`flex flex-col items-center gap-1 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${workspace === "video" ? "bg-gold text-black shadow-[3px_3px_0px_#000]" : "bg-white text-charcoal/70"}`}>
            <Video size={16} /> Video with Music
          </button>
          <button data-testid="viz-ws-audio" onClick={() => setWorkspace("audio")}
            className={`flex flex-col items-center gap-1 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${workspace === "audio" ? "bg-[#B16CFF] text-white shadow-[3px_3px_0px_#000]" : "bg-white text-charcoal/70"}`}>
            <Wand2 size={16} /> Audio Only / No Video
          </button>
        </div>

        {/* ============ WORKSPACE 1 · VIDEO WITH MUSIC ============ */}
        {workspace === "video" && (
          <div className="space-y-3" data-testid="viz-ws1">
            <input ref={bgVidRef} type="file" accept="video/*" hidden onChange={onBgVideoDevice} data-testid="viz-video-input" />
            <div className={`${box} p-3 space-y-2`}>
              <p className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60"><Clapperboard size={12} /> Your video</p>
              {!bgVideoId ? (
                <div className="flex gap-2">
                  <button data-testid="viz-video-device" onClick={() => { if (requireName()) bgVidRef.current?.click(); }} disabled={bgVideoBusy} className={`${btn} flex-1 bg-gold py-3 text-black disabled:opacity-60`}>{bgVideoBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Device</button>
                  <ProfileGalleryPickButton bucket="videos" testid="viz-video-vault" label="Profile Gallery" onFile={(f) => onBgVideoDevice({ target: { files: [f], value: "" } })} className={`${btn} flex-1 bg-charcoal py-3 text-[#00F5D4]`} />
                </div>
              ) : (
                <div className="flex items-center gap-2 border-2 border-black bg-[#FAF8F5] p-1.5" data-testid="viz-video-chip">
                  <span className="h-10 w-10 shrink-0 overflow-hidden border border-black bg-black">{bgVideoSrc ? <video src={bgVideoSrc} muted playsInline className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center"><Clapperboard size={12} className="text-white/40" /></span>}</span>
                  <span className="flex-1 font-mono text-[9px] font-black text-charcoal/70">Video ready · timeline locks to its length</span>
                  <button onClick={() => { setBgVideoId(null); setBgVideoSrc(null); }} data-testid="viz-video-remove" className="grid h-6 w-6 shrink-0 place-items-center border-2 border-black bg-white text-red-500"><X size={12} /></button>
                </div>
              )}
            </div>

            <div className={`${box} p-3 space-y-2`}>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60">Vibe prompt <span className="text-charcoal/40 normal-case tracking-normal">· style enhancement (Phase 2)</span></p>
              <textarea data-testid="viz-ws1-vibe" value={vibePrompt} onChange={(e) => setVibePrompt(e.target.value)} rows={2} maxLength={3000}
                placeholder="e.g. warm film grain, teal-orange grade, dreamy slow-mo feel"
                className="w-full resize-y overflow-y-auto min-h-[4.2rem] max-h-72 border-2 border-black bg-white p-2 font-mono text-[11px] leading-relaxed outline-none focus:bg-[#FFFDF5]" />
            </div>

            {/* Overlay audio toggle + 3-way source */}
            <div className={`${box} p-3 space-y-2`}>
              <button data-testid="viz-overlay-toggle" onClick={() => setOverlayAudio((v) => !v)} aria-pressed={overlayAudio}
                className={`flex w-full items-center gap-2.5 border-2 border-black px-3 py-2 text-left ${overlayAudio ? "bg-[#00A6A6] text-white" : "bg-white text-charcoal"}`}>
                <Music2 size={14} className="shrink-0" />
                <span className="flex-1"><span className="block font-mono text-[10px] font-black uppercase tracking-widest">Add / overlay custom audio</span><span className="block font-mono text-[8px] opacity-70">{overlayAudio ? "Score the video with a chosen track" : "Off · uses the video's own audio (Phase 2)"}</span></span>
                <span className={`grid h-5 w-9 shrink-0 items-center rounded-full border-2 border-black px-0.5 ${overlayAudio ? "bg-black" : "bg-white"}`}><span className={`h-3 w-3 rounded-full transition-transform ${overlayAudio ? "translate-x-4 bg-[#00F5D4]" : "translate-x-0 bg-charcoal"}`} /></span>
              </button>
              {overlayAudio && (
                <SourcePicker workspace="video" songId={songId} songName={songName} requireName={requireName}
                  audioRef={audioRef} onAudioDevice={onAudioDevice} loadCommAudio={loadCommAudio}
                  commOpen={commOpen} commAudio={commAudio} pickCommAudio={pickCommAudio}
                  openMusicStudio={openMusicStudio} clearSong={() => { setSongId(null); setSongName(""); }} />
              )}
            </div>
          </div>
        )}

        {/* ============ WORKSPACE 2 · AUDIO ONLY ============ */}
        {workspace === "audio" && (
          <div className="space-y-3" data-testid="viz-ws2">
            {/* Step 1 · Length */}
            <div className={`${box} p-3 space-y-2`}>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60">1 · Length</p>
              <div className="grid grid-cols-4 gap-1.5" data-testid="viz-duration">
                {DURATIONS.map(([v, lbl]) => (
                  <button key={String(v)} data-testid={`viz-dur-${v}`} onClick={() => setDurationMode(v)}
                    className={`${btn} py-2.5 ${durationMode === v ? "bg-gold text-black" : "bg-white text-charcoal"}`}>{lbl}</button>
                ))}
              </div>
              {durationMode === "full" && (
                <div className="flex items-center gap-1.5 border-2 border-dashed border-[#C2410C] bg-[#FFF4EC] p-2" data-testid="viz-full-warning">
                  <AlertTriangle size={13} className="shrink-0 text-[#C2410C]" />
                  <p className="font-mono text-[8px] font-black leading-relaxed text-[#C2410C]">Full-length visuals cover the whole song — renders take longer and use more AI credits.</p>
                </div>
              )}
            </div>

            {/* Step 2 · Track source */}
            <div className={`${box} p-3 space-y-2`}>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60">2 · Track</p>
              <input ref={audioRef} type="file" accept="audio/*" hidden onChange={onAudioDevice} data-testid="viz-audio-input" />
              <SourcePicker workspace="audio" songId={songId} songName={songName} requireName={requireName}
                audioRef={audioRef} onAudioDevice={onAudioDevice} loadCommAudio={loadCommAudio}
                commOpen={commOpen} commAudio={commAudio} pickCommAudio={pickCommAudio}
                openMusicStudio={openMusicStudio} clearSong={() => { setSongId(null); setSongName(""); }} />
            </div>

            {/* Step 3 · Visual seed */}
            <div className={`${box} p-3 space-y-2`}>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60">3 · Visuals</p>
              <textarea data-testid="viz-ws2-vibe" value={vibePrompt} onChange={(e) => setVibePrompt(e.target.value)} rows={3} maxLength={3000}
                placeholder="Describe the video — AI paints & animates the scenes to your song."
                className="w-full resize-y overflow-y-auto min-h-[6rem] max-h-80 border-2 border-black bg-white p-2 font-mono text-[11px] leading-relaxed outline-none focus:bg-[#FFFDF5]" />
              <div className="grid grid-cols-4 gap-1.5" data-testid="viz-ws2-style">
                {[["auto", "Auto"], ["realistic", "Real"], ["anime", "Anime"], ["cartoon", "Cartoon"]].map(([v, lbl]) => (
                  <button key={v} data-testid={`viz-ws2-style-${v}`} onClick={() => setVibeStyle(v)} className={`${btn} py-2 ${vibeStyle === v ? "bg-[#B16CFF] text-white" : "bg-white text-charcoal"}`}>{lbl}</button>
                ))}
              </div>
              <input ref={refImgRef} type="file" accept="image/*" hidden onChange={onRefImage} data-testid="viz-refimg-input" />
              {!refImageId ? (
                <div className="flex gap-1.5" data-testid="viz-refimg-sources">
                  <button data-testid="viz-refimg-device" onClick={() => { if (requireName()) refImgRef.current?.click(); }} className={`${btn} flex-1 bg-white py-2.5 text-charcoal`}>
                    <Upload size={12} /> Device
                  </button>
                  <ProfileGalleryPickButton bucket="images" testid="viz-refimg-gallery" label="Profile Gallery"
                    onFile={(f) => onRefImage({ target: { files: [f], value: "" } })} className={`${btn} flex-1 bg-white py-2.5 text-charcoal`} />
                </div>
              ) : (
                <div className="flex items-center gap-2 border-2 border-black bg-[#FAF8F5] p-1.5" data-testid="viz-refimg-chip">
                  <span className="h-9 w-9 shrink-0 overflow-hidden border border-black bg-black"><img src={refImageSrc} alt="ref" className="h-full w-full object-cover" /></span>
                  <span className="flex-1 font-mono text-[8px] font-black text-charcoal/60">Reference saved · guides Phase-2 composition</span>
                  <button onClick={() => { setRefImageId(null); setRefImageSrc(null); }} data-testid="viz-refimg-remove" className="grid h-6 w-6 shrink-0 place-items-center border-2 border-black bg-white text-red-500"><X size={12} /></button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* NEXT */}
        <button data-testid="viz-next" onClick={goAlign} disabled={!canNext}
          className={`${btn} w-full bg-charcoal py-4 text-gold disabled:opacity-40`}>
          Next · Align &amp; Style <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

/* Shared 3-way audio source picker: Device · Community · Singer AI */
function SourcePicker({ workspace, songId, songName, requireName, audioRef, onAudioDevice, loadCommAudio, commOpen, commAudio, pickCommAudio, openMusicStudio, clearSong }) {
  return (
    <div className="space-y-2" data-testid={`viz-source-${workspace}`}>
      {songId ? (
        <div className="flex items-center gap-2 border-2 border-[#00A6A6] bg-[#00A6A6]/10 p-1.5" data-testid="viz-song-chip">
          <Music2 size={13} className="shrink-0 text-[#00A6A6]" />
          <span className="flex-1 truncate font-mono text-[10px] font-black text-charcoal/80">{songName || "Track ready"}</span>
          <button onClick={clearSong} data-testid="viz-song-clear" className="grid h-6 w-6 shrink-0 place-items-center border-2 border-black bg-white text-red-500"><X size={12} /></button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-1.5">
            {workspace === "audio"
              ? <button data-testid="viz-src-device" onClick={() => { if (requireName()) audioRef.current?.click(); }} className={`${btn} bg-gold py-2.5 text-black`}><Upload size={12} /> Device</button>
              : <label data-testid="viz-src-device" onClick={(e) => { if (!requireName()) e.preventDefault(); }} className={`${btn} cursor-pointer bg-gold py-2.5 text-black`}><Upload size={12} /> Device<input type="file" accept="audio/*" hidden onChange={onAudioDevice} /></label>}
            <button data-testid="viz-src-community" onClick={loadCommAudio} className={`${btn} bg-white py-2.5 text-charcoal ${commOpen ? "ring-2 ring-[#00A6A6]" : ""}`}><Users size={12} /> Shared</button>
            <button data-testid="viz-src-singer" onClick={openMusicStudio} className={`${btn} bg-[#B16CFF] py-2.5 text-white`}><Sparkles size={12} /> Singer AI</button>
          </div>
          {commOpen && (
            <div className="max-h-40 space-y-1 overflow-y-auto border-2 border-black bg-[#FAF8F5] p-1.5" data-testid="viz-comm-list">
              {commAudio === null ? <div className="grid place-items-center py-3"><Loader2 size={14} className="animate-spin text-charcoal/40" /></div>
                : commAudio.length === 0 ? <p className="py-2 text-center font-mono text-[9px] text-charcoal/50">No shared songs yet.</p>
                  : commAudio.map((it) => (
                    <button key={it.id} data-testid={`viz-comm-${it.id}`} onClick={() => pickCommAudio(it)} className="flex w-full items-center gap-2 border-2 border-black bg-white px-2 py-1.5 text-left hover:bg-gold/20">
                      <Music2 size={11} className="shrink-0 text-[#00A6A6]" /><span className="flex-1 truncate font-mono text-[10px] font-black">{it.title || it.filename || "Shared track"}</span>{it.by && <span className="shrink-0 font-mono text-[8px] text-charcoal/40">@{it.by}</span>}
                    </button>
                  ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
